import useAlertQueue from 'hooks/useAlertQueue';
import type { MouseEventHandler } from 'react';
import Button, { type ButtonProps } from 'storybookConfig/stories/molecules/Button';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import { useCopyToClipboard } from 'usehooks-ts';

enum ButtonVariant {
  None = 'none',
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
}

interface ButtonComponentProps extends ButtonProps {
  variant?: `${ButtonVariant}`;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children?: React.ReactNode;
}

const ButtonComponent = ({ variant, ...props }: ButtonComponentProps) => {
  if (variant === ButtonVariant.Primary) return <PrimaryButton {...props} />;
  if (variant === ButtonVariant.Secondary) return <SecondaryButton {...props} />;
  if (variant === ButtonVariant.Tertiary) return <TertiaryButton {...props} />;

  return <Button {...props} />;
};

interface CopyTextButtonProps extends ButtonProps {
  /**
   * The text to copy
   */
  text: string;
  /**
   * The variant of the button
   */
  variant?: `${ButtonVariant}`;
  /**
   * The text to display in the success alert
   */
  successAlertText?: string;
  /**
   * The function to call after the text is copied
   */
  onCopy?: () => void;
  /**
   * The button text
   */
  children?: React.ReactNode;
}

/**
 * A button that copies text to the clipboard. Provides a `variant`
 * prop that can be used to render a `PrimaryButton`, `SecondaryButton`,
 * or `TertiaryButton` instead of a basic `Button`. All additional props
 * are passed to the underlying button component.
 */
const CopyTextButton = ({
  text,
  onCopy,
  successAlertText,
  children,
  ...buttonProps
}: CopyTextButtonProps) => {
  const [, copyText] = useCopyToClipboard();
  const alertQueue = useAlertQueue();

  const onClick = () => {
    copyText(text);
    if (onCopy) onCopy();
    alertQueue.addSuccessAlert('Success', successAlertText ?? `Copied to clipboard`);
  };

  return (
    <ButtonComponent $iconName="copy" onClick={onClick} {...buttonProps}>
      {children}
    </ButtonComponent>
  );
};

export default CopyTextButton;
